import ProductPreview, { HOVER_INTENT_MS } from './golden-product-preview';

/**
 * GOLDEN — EXACT port of Codrops ProductGrid, scoped to ONE 8-item block root.
 * Moved verbatim from the validated Hover Reference Test. Do not tune.
 */
export default class ProductGrid {
  ui: { products: HTMLElement[]; containerLeft: HTMLElement; containerRight: HTMLElement };

  productPreviewLeft: ProductPreview | null = null;

  productPreviewRight: ProductPreview | null = null;

  activeProduct: HTMLElement | null = null;

  hoverDelay: number | null = null;

  private locked = false;

  private awaitFreshMove = false;

  private cleanups: (() => void)[] = [];

  constructor(root: HTMLElement) {
    this.ui = {
      products: Array.from(root.querySelectorAll<HTMLElement>('.product')),
      containerLeft: root.querySelector<HTMLElement>('.product-preview.--left')!,
      containerRight: root.querySelector<HTMLElement>('.product-preview.--right')!,
    };

    this.init();
  }

  init() {
    this.productPreviewLeft = new ProductPreview({
      container: this.ui.containerRight,
      products: this.ui.products.filter((_, i) => i % 4 === 2 || i % 4 === 3),
    });

    this.productPreviewRight = new ProductPreview({
      container: this.ui.containerLeft,
      products: this.ui.products.filter((_, i) => i % 4 === 0 || i % 4 === 1),
    });

    this.addEvents();
  }

  addEvents() {
    let t: number | null = null;
    const onResize = () => {
      if (this.hoverDelay) { clearTimeout(this.hoverDelay); this.hoverDelay = null; }
      if (this.activeProduct) { this.getProductSide(this.activeProduct).timeline?.pause(0); this.activeProduct = null; }
      if (t !== null) window.clearTimeout(t);
      t = window.setTimeout(() => {
        this.productPreviewRight?.onResize();
        this.productPreviewLeft?.onResize();
      }, 100);
    };
    window.addEventListener('resize', onResize);
    const onMove = () => { this.awaitFreshMove = false; };
    window.addEventListener('pointermove', onMove);
    this.cleanups.push(() => window.removeEventListener('pointermove', onMove));
    this.cleanups.push(() => {
      window.removeEventListener('resize', onResize);
      if (t !== null) window.clearTimeout(t);
    });

    this.ui.products.forEach((product) => {
      const previewController = this.getProductSide(product);
      const enter = () => {
        if (this.locked || this.awaitFreshMove) return;
        if (window.matchMedia('(max-width: 767px), (hover: none)').matches) return;
        this.productMouseEnter(product, previewController);
      };
      const leave = () => this.productMouseLeave();
      product.addEventListener('mouseenter', enter);
      product.addEventListener('mouseleave', leave);
      this.cleanups.push(() => {
        product.removeEventListener('mouseenter', enter);
        product.removeEventListener('mouseleave', leave);
      });
    });
  }

  getProductSide = (product: HTMLElement) => {
    const i = Number(product.dataset.index);
    const isLeft = i % 4 === 0 || i % 4 === 1;

    return (isLeft ? this.productPreviewLeft : this.productPreviewRight)!;
  };

  productMouseEnter(product: HTMLElement, preview: ProductPreview) {
    if (this.hoverDelay) {
      clearTimeout(this.hoverDelay);
      this.hoverDelay = null;
    }

    this.hoverDelay = window.setTimeout(() => {
      this.activeProduct = product;
      preview.setProduct(product);
      this.hoverDelay = null;
    }, HOVER_INTENT_MS);
  }

  productMouseLeave() {
    if (this.hoverDelay) {
      clearTimeout(this.hoverDelay);
      this.hoverDelay = null;
    }

    if (this.activeProduct) {
      const preview = this.getProductSide(this.activeProduct);
      preview.setProduct(null);
      this.activeProduct = null;
    }
  }

  beginDetailHandoff() {
    this.locked = true;
    this.productMouseLeave();
  }

  endDetailHandoff() {
    this.locked = false;
    this.awaitFreshMove = true;
  }

  destroy() {
    if (this.hoverDelay) clearTimeout(this.hoverDelay);
    this.cleanups.forEach((fn) => fn());
    this.productPreviewLeft?.destroy();
    this.productPreviewRight?.destroy();
  }
}